// Гейт подписки на канал: одна реализация правила на фронт и бэк.
//
// Правило (решение основателя): пользоваться мини-аппой можно, если подписан на канал.
// У новенькой есть льготный период после регистрации — за это время она успевает
// осмотреться и понять, зачем вообще подписываться. Доступ закрывается ТОЛЬКО когда
// одновременно: льготный период истёк И подписки нет.
//
// Держим здесь, а не по обе стороны отдельно: разъехавшийся на минуту порог означал бы,
// что фронт уводит на экран-заглушку, а бэк считает доступ открытым (или наоборот).

/** Льготный период после регистрации, минуты. */
export const CHANNEL_GATE_GRACE_MINUTES = 10

/** Он же в миллисекундах — в этом виде считается разница дат. */
export const CHANNEL_GATE_GRACE_MS = CHANNEL_GATE_GRACE_MINUTES * 60 * 1000

/**
 * Статусы `getChatMember`, которые считаются подпиской.
 *
 * `restricted` сюда сознательно не входит: человек в канале, но ограничен, и по правилу
 * основателя подпиской это не считается. `left` / `kicked` — очевидно нет.
 */
export const SUBSCRIBED_CHAT_MEMBER_STATUSES = ['member', 'administrator', 'creator'] as const
export type SubscribedChatMemberStatus = (typeof SUBSCRIBED_CHAT_MEMBER_STATUSES)[number]

/** Трактовка статуса из Telegram. Неизвестный статус — не подписка. */
export const isSubscribedChatMemberStatus = (status: string | null | undefined): boolean =>
  status !== null &&
  status !== undefined &&
  (SUBSCRIBED_CHAT_MEMBER_STATUSES as readonly string[]).includes(status)

/**
 * Почему доступ открыт или закрыт. Причина нужна фронту (что показывать) и логам
 * (сколько людей упирается в гейт против того, сколько проходит по льготному периоду).
 */
export const CHANNEL_ACCESS_REASONS = [
  'gate_disabled',
  'subscribed',
  'grace_period',
  'check_failed',
  'not_subscribed',
] as const
export type ChannelAccessReason = (typeof CHANNEL_ACCESS_REASONS)[number]

export interface ResolveChannelAccessInput {
  /** false — канал не настроен (нет REQUIRED_CHANNEL_ID), гейт выключен целиком. */
  isGateEnabled: boolean
  /**
   * Результат проверки подписки. `null` — проверить не удалось (Telegram ответил ошибкой,
   * бот не админ канала, сеть легла): трактуем в пользу пользователя и не блокируем.
   */
  isSubscribed: boolean | null
  /** Дата регистрации пользователя. */
  registeredAt: Date
  /** «Сейчас» — передаётся явно, чтобы правило оставалось чистой функцией. */
  now: Date
}

export interface ChannelAccessResult {
  allowed: boolean
  reason: ChannelAccessReason
}

/** Момент, когда льготный период заканчивается. */
export const getChannelGraceEndsAt = (registeredAt: Date): Date =>
  new Date(registeredAt.getTime() + CHANNEL_GATE_GRACE_MS)

/** Льготный период ещё идёт (ровно на границе — уже закончился). */
export const isWithinChannelGracePeriod = (registeredAt: Date, now: Date): boolean =>
  now.getTime() - registeredAt.getTime() < CHANNEL_GATE_GRACE_MS

/**
 * Итог гейта. Порядок веток и есть правило: выключенный гейт и подписка пропускают всегда,
 * дальше — поблажки (не смогли проверить, льготный период), и только потом отказ.
 */
export const resolveChannelAccess = ({
  isGateEnabled,
  isSubscribed,
  registeredAt,
  now,
}: ResolveChannelAccessInput): ChannelAccessResult => {
  if (!isGateEnabled) return { allowed: true, reason: 'gate_disabled' }
  if (isSubscribed === true) return { allowed: true, reason: 'subscribed' }
  if (isSubscribed === null) return { allowed: true, reason: 'check_failed' }
  if (isWithinChannelGracePeriod(registeredAt, now))
    return { allowed: true, reason: 'grace_period' }
  return { allowed: false, reason: 'not_subscribed' }
}
